import { type Col, type EntKind, type Node, Op } from '@revelio/data-access';

import {
  type Filter,
  FilterType,
  isMultiFilter,
  isSegmentedFilter,
  type TreeFilter,
} from '../../types/filters';
import { resolveCompositionV3EntForSelection } from '../composition-v3/getCompositionV3Entities';

/**
 * An entity kind as the expression column of the same name. The schema keeps
 * the two enums apart; the assignment stops compiling if a kind has no column.
 */
const colOf = (kind: EntKind): Col => {
  const col: `${Col}` = kind;
  return col as Col;
};

export const catLeaf = (col: Col, id: number): Node => ({
  leaf: { cat: { col, id } },
});

const treeInstancesOf = (filter: Filter): TreeFilter[] => {
  if (isMultiFilter(filter)) {
    return filter.instances.filter(
      (instance): instance is TreeFilter => instance.type === FilterType.TREE
    );
  }
  if (isSegmentedFilter(filter)) {
    return filter.wrappedFilter.type === FilterType.TREE
      ? [filter.wrappedFilter]
      : [];
  }
  return filter.type === FilterType.TREE ? [filter] : [];
};

export const combine = (op: Op, operands: Node[]): Node | null => {
  if (operands.length <= 1) return operands[0] ?? null;
  return { op: { op, operands } };
};

const negate = (operand: Node): Node => ({
  op: { op: Op.Not, operands: [operand] },
});

const instanceNode = (instance: TreeFilter): Node | null => {
  const leaves = instance.selections.flatMap((selection): Node[] => {
    const ent = resolveCompositionV3EntForSelection(selection);
    return ent === null ? [] : [catLeaf(colOf(ent.EntType), ent.EntID)];
  });
  const anyOf = combine(Op.Or, leaves);
  return anyOf !== null && instance.isNot ? negate(anyOf) : anyOf;
};

/** Each entity filter, or each instance of a multi one, as one operand. */
export const filterOperands = (filters: Filter[]): Node[] =>
  filters.flatMap(treeInstancesOf).flatMap((instance) => {
    const node = instanceNode(instance);
    return node === null ? [] : [node];
  });

/**
 * Talent Discovery's semantics: a filter (or each instance of a multi filter)
 * matches any of its selections, "is not" negates it, and filters and
 * instances must all match. It applies to every requested entity as it is —
 * unlike `stripEntitiesFromFilters`, never dropping a selection that names
 * one: a company picked by a positive-only top-N request still has to pass
 * "is not", and dropping one value of "is any of" would leave the others to
 * exclude the entity it named.
 */
export const buildFiltersExpr = (filters: Filter[]): Node | null =>
  combine(Op.And, filterOperands(filters));
